import Tooltip, { type TooltipProps } from "@mui/material/Tooltip";
import { type DataGridProProps, GridRow, type GridRowProps, type RowPropsOverrides } from "@mui/x-data-grid-pro";
import type { CSSProperties } from "react";
import { useMemo } from "react";
import type { CaseItem } from "src/types/case-items";
import { MigratedBannerBody, MigratedBannerTitle } from "./MigratedBannerText";

import css from "./useCustomRowDisplay.module.css";

const tooltipSlotProps: TooltipProps["slotProps"] = {
  tooltip: { sx: { scale: 1.25 } },
  // Lift the tooltip clear of the cursor, which otherwise sits on top of the first line
  popper: { modifiers: [{ name: "offset", options: { offset: [0, 18] } }] },
};
const hrStyle: CSSProperties = { marginBlock: "2px" };

/**
 * Case item rows whose work packet has been migrated out of CGOps are read-only:
 * they get the migrated styling, a tooltip explaining why, and swallow row clicks.
 */
export function useMigratedCaseItemRowDisplay() {
  const slots = useMemo<DataGridProProps["slots"]>(() => ({ row: createMigratedAwareCaseItemRow() }), []);

  return { slots };
}

function createMigratedAwareCaseItemRow() {
  return function MigratedAwareCaseItemRow(props: GridRowProps & RowPropsOverrides) {
    const row = props.row as CaseItem | undefined;

    if (!row?.migratedAt) return <GridRow {...props} />;

    const tooltipContent = (
      <div>
        <p>
          <b>
            <MigratedBannerTitle />
          </b>
        </p>
        <p>
          <i>Since {row.migratedAt.toDate().toLocaleDateString("en-us", { dateStyle: "medium" })}</i>
        </p>
        <hr style={hrStyle} />
        <p>
          <MigratedBannerBody />
        </p>
      </div>
    );

    return (
      <Tooltip title={tooltipContent} followCursor placement="top" slotProps={tooltipSlotProps}>
        <GridRow
          {...props}
          onClick={undefined}
          onDoubleClick={undefined}
          aria-disabled
          className={css.migratedPacket}
        />
      </Tooltip>
    );
  };
}
